/**
 * Country & region configuration for WYMR global transparency platform.
 * Excludes: North Korea, Belarus, and UN Western Asia subregion nations.
 */

export const EXCLUDED_COUNTRY_CODES = new Set([
  'KP', // North Korea
  'BY', // Belarus
  // UN Western Asia / Middle East
  'AM', 'AZ', 'BH', 'CY', 'GE', 'IQ', 'IR', 'IL', 'JO', 'KW', 'LB', 'OM', 'PS', 'QA', 'SA', 'SY', 'TR', 'AE', 'YE',
]);

export const REGIONS = [
  { id: 'north_america', labelKey: 'region.northAmerica' },
  { id: 'latin_america', labelKey: 'region.latinAmerica' },
  { id: 'europe', labelKey: 'region.europe' },
  { id: 'africa', labelKey: 'region.africa' },
  { id: 'east_asia', labelKey: 'region.eastAsia' },
  { id: 'southeast_asia', labelKey: 'region.southeastAsia' },
  { id: 'south_asia', labelKey: 'region.southAsia' },
  { id: 'central_asia', labelKey: 'region.centralAsia' },
  { id: 'oceania', labelKey: 'region.oceania' },
];

/** @type {Array<{code:string,name:string,region:string,politicalEntityLabel:string,languages:string[]}>} */
export const COUNTRIES = [
  // North America
  { code: 'US', name: 'United States', region: 'north_america', politicalEntityLabel: 'PAC', languages: ['en'] },
  { code: 'CA', name: 'Canada', region: 'north_america', politicalEntityLabel: 'Registered Association', languages: ['en', 'fr'] },
  { code: 'MX', name: 'Mexico', region: 'north_america', politicalEntityLabel: 'Partido Político', languages: ['es'] },

  // Latin America
  { code: 'BR', name: 'Brazil', region: 'latin_america', politicalEntityLabel: 'Partido Político', languages: ['pt'] },
  { code: 'AR', name: 'Argentina', region: 'latin_america', politicalEntityLabel: 'Partido Político', languages: ['es'] },
  { code: 'CL', name: 'Chile', region: 'latin_america', politicalEntityLabel: 'Partido Político', languages: ['es'] },
  { code: 'CO', name: 'Colombia', region: 'latin_america', politicalEntityLabel: 'Partido Político', languages: ['es'] },
  { code: 'PE', name: 'Peru', region: 'latin_america', politicalEntityLabel: 'Partido Político', languages: ['es'] },
  { code: 'VE', name: 'Venezuela', region: 'latin_america', politicalEntityLabel: 'Partido Político', languages: ['es'] },
  { code: 'EC', name: 'Ecuador', region: 'latin_america', politicalEntityLabel: 'Partido Político', languages: ['es'] },
  { code: 'UY', name: 'Uruguay', region: 'latin_america', politicalEntityLabel: 'Partido Político', languages: ['es'] },
  { code: 'PY', name: 'Paraguay', region: 'latin_america', politicalEntityLabel: 'Partido Político', languages: ['es'] },
  { code: 'BO', name: 'Bolivia', region: 'latin_america', politicalEntityLabel: 'Partido Político', languages: ['es'] },
  { code: 'CR', name: 'Costa Rica', region: 'latin_america', politicalEntityLabel: 'Partido Político', languages: ['es'] },
  { code: 'PA', name: 'Panama', region: 'latin_america', politicalEntityLabel: 'Partido Político', languages: ['es'] },
  { code: 'GT', name: 'Guatemala', region: 'latin_america', politicalEntityLabel: 'Partido Político', languages: ['es'] },
  { code: 'DO', name: 'Dominican Republic', region: 'latin_america', politicalEntityLabel: 'Partido Político', languages: ['es'] },
  { code: 'CU', name: 'Cuba', region: 'latin_america', politicalEntityLabel: 'Partido Político', languages: ['es'] },
  { code: 'JM', name: 'Jamaica', region: 'latin_america', politicalEntityLabel: 'Political Party', languages: ['en'] },
  { code: 'TT', name: 'Trinidad and Tobago', region: 'latin_america', politicalEntityLabel: 'Political Party', languages: ['en'] },

  // Europe
  { code: 'GB', name: 'United Kingdom', region: 'europe', politicalEntityLabel: 'Political Party', languages: ['en'] },
  { code: 'DE', name: 'Germany', region: 'europe', politicalEntityLabel: 'Political Party', languages: ['de'] },
  { code: 'FR', name: 'France', region: 'europe', politicalEntityLabel: 'Political Party', languages: ['fr'] },
  { code: 'IT', name: 'Italy', region: 'europe', politicalEntityLabel: 'Political Party', languages: ['it'] },
  { code: 'ES', name: 'Spain', region: 'europe', politicalEntityLabel: 'Political Party', languages: ['es'] },
  { code: 'NL', name: 'Netherlands', region: 'europe', politicalEntityLabel: 'Political Party', languages: ['nl'] },
  { code: 'BE', name: 'Belgium', region: 'europe', politicalEntityLabel: 'Political Party', languages: ['nl', 'fr'] },
  { code: 'CH', name: 'Switzerland', region: 'europe', politicalEntityLabel: 'Political Party', languages: ['de', 'fr'] },
  { code: 'AT', name: 'Austria', region: 'europe', politicalEntityLabel: 'Political Party', languages: ['de'] },
  { code: 'SE', name: 'Sweden', region: 'europe', politicalEntityLabel: 'Political Party', languages: ['sv'] },
  { code: 'NO', name: 'Norway', region: 'europe', politicalEntityLabel: 'Political Party', languages: ['no'] },
  { code: 'DK', name: 'Denmark', region: 'europe', politicalEntityLabel: 'Political Party', languages: ['da'] },
  { code: 'FI', name: 'Finland', region: 'europe', politicalEntityLabel: 'Political Party', languages: ['fi'] },
  { code: 'PL', name: 'Poland', region: 'europe', politicalEntityLabel: 'Political Party', languages: ['pl'] },
  { code: 'CZ', name: 'Czech Republic', region: 'europe', politicalEntityLabel: 'Political Party', languages: ['cs'] },
  { code: 'PT', name: 'Portugal', region: 'europe', politicalEntityLabel: 'Political Party', languages: ['pt'] },
  { code: 'IE', name: 'Ireland', region: 'europe', politicalEntityLabel: 'Political Party', languages: ['en'] },
  { code: 'GR', name: 'Greece', region: 'europe', politicalEntityLabel: 'Political Party', languages: ['el'] },
  { code: 'RO', name: 'Romania', region: 'europe', politicalEntityLabel: 'Political Party', languages: ['ro'] },
  { code: 'HU', name: 'Hungary', region: 'europe', politicalEntityLabel: 'Political Party', languages: ['hu'] },
  { code: 'UA', name: 'Ukraine', region: 'europe', politicalEntityLabel: 'Political Party', languages: ['uk'] },
  { code: 'RU', name: 'Russia', region: 'europe', politicalEntityLabel: 'Political Party', languages: ['ru'] },

  // Africa
  { code: 'ZA', name: 'South Africa', region: 'africa', politicalEntityLabel: 'Political Party', languages: ['en'] },
  { code: 'NG', name: 'Nigeria', region: 'africa', politicalEntityLabel: 'Political Party', languages: ['en'] },
  { code: 'EG', name: 'Egypt', region: 'africa', politicalEntityLabel: 'Political Party', languages: ['ar'] },
  { code: 'KE', name: 'Kenya', region: 'africa', politicalEntityLabel: 'Political Party', languages: ['en'] },
  { code: 'GH', name: 'Ghana', region: 'africa', politicalEntityLabel: 'Political Party', languages: ['en'] },
  { code: 'ET', name: 'Ethiopia', region: 'africa', politicalEntityLabel: 'Political Party', languages: ['am'] },
  { code: 'MA', name: 'Morocco', region: 'africa', politicalEntityLabel: 'Political Party', languages: ['ar', 'fr'] },
  { code: 'TN', name: 'Tunisia', region: 'africa', politicalEntityLabel: 'Political Party', languages: ['ar', 'fr'] },
  { code: 'DZ', name: 'Algeria', region: 'africa', politicalEntityLabel: 'Political Party', languages: ['ar', 'fr'] },
  { code: 'TZ', name: 'Tanzania', region: 'africa', politicalEntityLabel: 'Political Party', languages: ['en', 'sw'] },
  { code: 'UG', name: 'Uganda', region: 'africa', politicalEntityLabel: 'Political Party', languages: ['en'] },
  { code: 'SN', name: 'Senegal', region: 'africa', politicalEntityLabel: 'Political Party', languages: ['fr'] },
  { code: 'CI', name: 'Ivory Coast', region: 'africa', politicalEntityLabel: 'Political Party', languages: ['fr'] },
  { code: 'AO', name: 'Angola', region: 'africa', politicalEntityLabel: 'Political Party', languages: ['pt'] },
  { code: 'MZ', name: 'Mozambique', region: 'africa', politicalEntityLabel: 'Political Party', languages: ['pt'] },

  // East Asia
  { code: 'CN', name: 'China', region: 'east_asia', politicalEntityLabel: 'Political Party', languages: ['zh'] },
  { code: 'JP', name: 'Japan', region: 'east_asia', politicalEntityLabel: 'Political Party', languages: ['ja'] },
  { code: 'KR', name: 'South Korea', region: 'east_asia', politicalEntityLabel: 'Political Party', languages: ['ko'] },
  { code: 'TW', name: 'Taiwan', region: 'east_asia', politicalEntityLabel: 'Political Party', languages: ['zh'] },
  { code: 'HK', name: 'Hong Kong', region: 'east_asia', politicalEntityLabel: 'Political Party', languages: ['zh', 'en'] },
  { code: 'MN', name: 'Mongolia', region: 'east_asia', politicalEntityLabel: 'Political Party', languages: ['mn'] },

  // Southeast Asia
  { code: 'SG', name: 'Singapore', region: 'southeast_asia', politicalEntityLabel: 'Political Party', languages: ['en', 'zh'] },
  { code: 'MY', name: 'Malaysia', region: 'southeast_asia', politicalEntityLabel: 'Political Party', languages: ['ms', 'en'] },
  { code: 'ID', name: 'Indonesia', region: 'southeast_asia', politicalEntityLabel: 'Political Party', languages: ['id'] },
  { code: 'TH', name: 'Thailand', region: 'southeast_asia', politicalEntityLabel: 'Political Party', languages: ['th'] },
  { code: 'VN', name: 'Vietnam', region: 'southeast_asia', politicalEntityLabel: 'Political Party', languages: ['vi'] },
  { code: 'PH', name: 'Philippines', region: 'southeast_asia', politicalEntityLabel: 'Political Party', languages: ['en', 'tl'] },
  { code: 'MM', name: 'Myanmar', region: 'southeast_asia', politicalEntityLabel: 'Political Party', languages: ['my'] },
  { code: 'KH', name: 'Cambodia', region: 'southeast_asia', politicalEntityLabel: 'Political Party', languages: ['km'] },
  { code: 'LA', name: 'Laos', region: 'southeast_asia', politicalEntityLabel: 'Political Party', languages: ['lo'] },

  // South Asia
  { code: 'IN', name: 'India', region: 'south_asia', politicalEntityLabel: 'Political Party', languages: ['hi', 'en'] },
  { code: 'PK', name: 'Pakistan', region: 'south_asia', politicalEntityLabel: 'Political Party', languages: ['ur', 'en'] },
  { code: 'BD', name: 'Bangladesh', region: 'south_asia', politicalEntityLabel: 'Political Party', languages: ['bn'] },
  { code: 'LK', name: 'Sri Lanka', region: 'south_asia', politicalEntityLabel: 'Political Party', languages: ['si', 'ta'] },
  { code: 'NP', name: 'Nepal', region: 'south_asia', politicalEntityLabel: 'Political Party', languages: ['ne'] },

  // Central Asia
  { code: 'KZ', name: 'Kazakhstan', region: 'central_asia', politicalEntityLabel: 'Political Party', languages: ['kk', 'ru'] },
  { code: 'UZ', name: 'Uzbekistan', region: 'central_asia', politicalEntityLabel: 'Political Party', languages: ['uz'] },

  // Oceania
  { code: 'AU', name: 'Australia', region: 'oceania', politicalEntityLabel: 'Political Party', languages: ['en'] },
  { code: 'NZ', name: 'New Zealand', region: 'oceania', politicalEntityLabel: 'Political Party', languages: ['en'] },
  { code: 'FJ', name: 'Fiji', region: 'oceania', politicalEntityLabel: 'Political Party', languages: ['en'] },
];

export const ENABLED_COUNTRIES = COUNTRIES.filter(c => !EXCLUDED_COUNTRY_CODES.has(c.code));

export const INTERNATIONAL_CODE = 'INT';
export const INTERNATIONAL_OPTION = {
  code: INTERNATIONAL_CODE,
  name: 'International / Global',
  region: 'global',
  politicalEntityLabel: 'Political Finance Entity',
  languages: ['en'],
};

export const ALL_COUNTRY_OPTIONS = [INTERNATIONAL_OPTION, ...ENABLED_COUNTRIES];

export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'es', name: 'Spanish', nativeName: 'Español' },
  { code: 'fr', name: 'French', nativeName: 'Français' },
  { code: 'de', name: 'German', nativeName: 'Deutsch' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português' },
  { code: 'zh', name: 'Chinese', nativeName: '中文' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語' },
  { code: 'ko', name: 'Korean', nativeName: '한국어' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski' },
];

export function getCountryByCode(code) {
  if (!code || code === INTERNATIONAL_CODE) return INTERNATIONAL_OPTION;
  return ENABLED_COUNTRIES.find(c => c.code === code) || INTERNATIONAL_OPTION;
}

export function getCountriesByRegion(regionId) {
  if (!regionId || regionId === 'all') return ENABLED_COUNTRIES;
  return ENABLED_COUNTRIES.filter(c => c.region === regionId);
}

export function getPoliticalEntityLabel(countryCode) {
  return getCountryByCode(countryCode).politicalEntityLabel || 'Political Finance Entity';
}

/** Build Supabase OR filter for country — works with country_code or legacy text fields */
export function countryFilterFields(countryCode) {
  const country = getCountryByCode(countryCode);
  if (countryCode === INTERNATIONAL_CODE) return null;
  return { code: countryCode, name: country.name };
}
